import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Collapse, FormControlLabel, IconButton,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import { listInstanceEvents } from '../../../services/stacksetsApi';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, insetPanelSx, sectionHeadingSx, emptyTextSx,
} from '../portalStyles';

const phaseColor = (phase) => ({
  current: 'success',
  outdated: 'warning',
  failed: 'error',
  deleted: 'default',
  skipped: 'default',
  pending: 'info',
  creating: 'info',
  updating: 'info',
  deleting: 'info',
}[phase] || 'default');

// CloudFormation resource-status → chip color (mirrors AWS console colouring).
const eventColor = (status) => {
  if (/FAILED|ROLLBACK/.test(status)) return 'error';
  if (/COMPLETE/.test(status)) return 'success';
  if (/IN_PROGRESS/.test(status)) return 'info';
  return 'default';
};

// InstanceEventsRow renders one StackSet instance row with an expandable panel
// showing the member-account CloudFormation stack's resource-level events (the
// AWS "Events" tab). The panel has a manual refresh button and an auto-refresh
// toggle that is ON by default — while auto-refresh is on and the row is
// expanded, the events re-poll every few seconds, a live timeline of resources
// being created/updated/deleted in that account.
export default function InstanceEventsRow({ instance, stackSetId, index = 0 }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true); // on by default
  const pollRef = useRef(null);

  const fetchEvents = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await listInstanceEvents(stackSetId, instance.accountId, instance.region);
      setEvents(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [stackSetId, instance.accountId, instance.region]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && events === null) fetchEvents(true);
  };

  // Auto-refresh while expanded and the toggle is on (default on). A background
  // refresh (showSpinner=false) never blanks the table.
  useEffect(() => {
    if (!open || !autoRefresh) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    pollRef.current = setInterval(() => fetchEvents(false), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open, autoRefresh, fetchEvents]);

  return (
    <>
      {/* The summary row draws the group's separator via bodyCellSx, exactly
          like every other portal table; the collapse row below adds its own
          only while it is open. */}
      <TableRow hover sx={rowSx(index)}>
        <TableCell sx={{ ...bodyCellSx, width: 40 }}>
          <IconButton size="small" onClick={toggle} aria-label="stack events">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={bodyCellSx}>{instance.accountId}</TableCell>
        <TableCell sx={bodyCellSx}>{instance.region}</TableCell>
        <TableCell sx={bodyCellSx}>
          <Chip size="small" label={formatStatusLabel(instance.phase)} sx={statusChipSx(intentFromColor(phaseColor(instance.phase)))} />
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, maxWidth: 380, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {instance.statusReason}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? `1px solid ${CK.border}` : 'none' }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ ...insetPanelSx, p: 2, my: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem' }}>Stack events</Typography>
                <Tooltip title="Refresh now">
                  <span>
                    <IconButton size="small" onClick={() => fetchEvents(true)} disabled={loading} aria-label="refresh stack events">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <FormControlLabel
                  sx={{ ml: 0.5, mr: 0 }}
                  control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
                  label={(
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                      Auto-refresh
                    </Typography>
                  )}
                />
                {autoRefresh && <Chip size="small" label="Live" sx={statusChipSx('info')} />}
                {loading && <CircularProgress size={14} thickness={5} sx={{ color: CK.blue }} />}
              </Box>
              {error && <Alert severity="error" sx={{ mb: 1, borderRadius: '8px' }}>{error}</Alert>}
              {events && events.length === 0 && !loading && (
                <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>
                  No stack events yet — the stack may not exist yet, or it was deleted.
                </Typography>
              )}
              {events && events.length > 0 && (
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={tableHeadRowSx}>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Time</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Status</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Type</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Logical ID</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((ev, i) => (
                        <TableRow key={`${ev.timestamp}-${ev.logicalResourceId}-${i}`} hover sx={rowSx(i)}>
                          <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                            {new Date(ev.timestamp).toLocaleTimeString()}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Chip size="small" label={formatStatusLabel(ev.resourceStatus)} sx={statusChipSx(intentFromColor(eventColor(ev.resourceStatus)))} />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{ev.resourceType}</TableCell>
                          <TableCell sx={bodyCellSx}>{ev.logicalResourceId}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {ev.resourceStatusReason}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
